import { useState, type Key } from 'react';
import { Table, Button, Typography, Space, Popconfirm, Drawer, App } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { EcnMeta, EcnLine } from '@bom/shared';
import { useSessionStore } from '../../stores/sessionStore';
import { useEcns, useEcn, useDeleteEcn, useResendEcnNotify, openEcnFile } from '../../api/ecns';
import { NoTenant } from '../../components/NoTenant';
import { fmtTime } from '../../lib/format';
import { LIST_PAGINATION, LIST_SCROLL_Y } from '../../lib/tablePagination';
import { ApiError } from '../../lib/apiClient';
import { EcnUploadModal } from './EcnUploadModal';
import { SendMailModal } from '../../components/SendMailModal';

function fmtDate(d?: string): string {
  if (!d) return '—';
  return d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d;
}

function EcnDetailDrawer({
  tid,
  ecnId,
  onClose,
}: {
  tid: string;
  ecnId?: string | undefined;
  onClose: () => void;
}) {
  const q = useEcn(tid, ecnId);
  return (
    <Drawer title="ECN 变更明细" open={!!ecnId} onClose={onClose} width={760} loading={q.isLoading}>
      <Table<EcnLine>
        rowKey="no"
        size="small"
        dataSource={q.data?.lines ?? []}
        pagination={false}
        columns={[
          { title: 'No', dataIndex: 'no', width: 50 },
          { title: '变更前料号', render: (_, r) => r.before.part_no || '—', width: 130 },
          { title: '变更前规格', render: (_, r) => r.before.spec || '—', ellipsis: true },
          { title: '变更后料号', render: (_, r) => r.after.part_no || '—', width: 130 },
          { title: '变更后规格', render: (_, r) => r.after.spec || '—', ellipsis: true },
          { title: '备注', dataIndex: 'remark', width: 90, render: (v?: string) => v ?? '—' },
        ]}
      />
    </Drawer>
  );
}

export function EcnListPage() {
  const { message } = App.useApp();
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const role = useSessionStore((s) => s.role);
  const isPlatformAdmin = useSessionStore((s) => s.user?.is_platform_admin ?? false);
  const canUpload = isPlatformAdmin || role === 'admin' || role === 'editor';
  const canDelete = isPlatformAdmin || role === 'admin';

  const ecns = useEcns(tid);
  const del = useDeleteEcn(tid ?? '');
  const resend = useResendEcnNotify(tid ?? '');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sendId, setSendId] = useState<string | undefined>();
  const [detailId, setDetailId] = useState<string | undefined>();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);

  const onBulkDelete = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((id) =>
        del.mutateAsync(id).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    setSelectedKeys([]);
    if (fail === 0) message.success(`已删除 ${ok} 条 ECN`);
    else message.warning(`删除 ${ok} 条，${fail} 条失败`);
  };

  if (!tid) return <NoTenant />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Text type="secondary">
          上传 ECN .xls
          自动解析变更；「明细」看变更前后，「原文件」可下载。变更也会汇入对应料号的时间线。
        </Typography.Text>
        <Space size="small">
          {canDelete && selectedKeys.length > 0 && (
            <Popconfirm
              title={`删除选中的 ${selectedKeys.length} 条 ECN？`}
              description="将连同其变更明细与料号时间线一并删除，不可恢复"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkDelete}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `删除中 ${delDone}/${selectedKeys.length}`
                  : `批量删除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
          {canUpload && (
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              上传 ECN
            </Button>
          )}
        </Space>
      </Space>

      <Table<EcnMeta>
        rowKey="id"
        loading={ecns.isLoading}
        dataSource={ecns.data ?? []}
        scroll={{ x: 'max-content', y: LIST_SCROLL_Y }}
        pagination={LIST_PAGINATION}
        {...(canDelete
          ? {
              rowSelection: {
                selectedRowKeys: selectedKeys,
                onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
              },
            }
          : {})}
        columns={[
          { title: '日期', dataIndex: 'ecn_date', width: 120, render: fmtDate },
          {
            title: '变更料号',
            dataIndex: 'affected_pcba',
            width: 180,
            render: (v?: string) => v ?? '—',
          },
          {
            title: '变更项目',
            key: 'change',
            width: 260,
            ellipsis: true,
            render: (_: unknown, r: EcnMeta) => r.change_summary || r.change_subject || '—',
          },
          { title: '变更项数', dataIndex: 'line_count', width: 90 },
          { title: '上传时间', dataIndex: 'created_at', width: 170, render: fmtTime },
          {
            title: '操作',
            key: 'action',
            width: 260,
            fixed: 'right',
            render: (_: unknown, r: EcnMeta) => (
              <Space size="small">
                <Button size="small" type="link" onClick={() => setDetailId(r.id)}>
                  明细
                </Button>
                {r.source_file_s3_key && (
                  <Button
                    size="small"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => openEcnFile(tid, r.id).catch(() => message.error('下载失败'))}
                  >
                    原文件
                  </Button>
                )}
                {canUpload && (
                  <Button
                    size="small"
                    type="link"
                    loading={resend.isPending && resend.variables?.ecnId === r.id}
                    onClick={() => setSendId(r.id)}
                  >
                    发送邮件
                  </Button>
                )}
                {canDelete && (
                  <Popconfirm
                    title="删除该 ECN？"
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() =>
                      del.mutate(r.id, {
                        onSuccess: () => message.success('已删除'),
                        onError: (e) =>
                          message.error(
                            `删除失败：${e instanceof ApiError ? e.message : (e as Error).message}`,
                          ),
                      })
                    }
                  >
                    <Button size="small" type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      {uploadOpen && (
        <EcnUploadModal tid={tid} open={uploadOpen} onClose={() => setUploadOpen(false)} />
      )}
      <EcnDetailDrawer tid={tid} ecnId={detailId} onClose={() => setDetailId(undefined)} />
      <SendMailModal
        open={!!sendId}
        title="发送 ECN 通知邮件"
        loading={resend.isPending}
        onCancel={() => setSendId(undefined)}
        onSend={(subject, body) => {
          if (!sendId) return;
          resend.mutate(
            { ecnId: sendId, ...(subject ? { subject } : {}), ...(body ? { body } : {}) },
            {
              onSuccess: (res) => {
                if (res.notified) message.success(`已发送通知（${res.recipients} 位收件人）`);
                else message.error(`发送失败：${res.error ?? '未知原因'}`);
                setSendId(undefined);
              },
              onError: (e) => message.error((e as Error).message || '发送失败'),
            },
          );
        }}
      />
    </Space>
  );
}
